import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Calendar, UserPlus } from "lucide-react";

export default function SyndicatAdherents() {
  const [items, setItems] = useState([]);
  const [available, setAvailable] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/syndicat/adherents"),
        api.get("/syndicat/entreprises-disponibles"),
      ]);
      setItems(a.data); setAvailable(b.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/syndicat/adherents", { entreprise_id: picked, note });
      toast.success("Entreprise affiliée");
      setOpen(false); setPicked(""); setNote(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (aid) => {
    if (!window.confirm("Retirer cette entreprise de votre syndicat ?")) return;
    try { await api.delete(`/syndicat/adherents/${aid}`); toast.success("Retirée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Affiliation"
        title="Entreprises adhérentes"
        sub="Affiliez les entreprises actives PAG à votre organisation syndicale."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="adherent-add-btn" className="h-11 bg-[#002FA7] text-white"><UserPlus className="h-4 w-4 mr-2" /> Affilier une entreprise</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Affilier une entreprise</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="adherent-form">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Entreprise</Label>
                  <Select value={picked} onValueChange={setPicked}>
                    <SelectTrigger data-testid="adherent-pick" className="h-11"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>
                      {available.length === 0 ? (
                        <SelectItem value="none" disabled>Aucune entreprise disponible</SelectItem>
                      ) : available.map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.name} ({e.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Note interne (optionnel)</Label>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="adherent-note" />
                </div>
                <DialogFooter><Button type="submit" disabled={!picked || picked === "none"} data-testid="adherent-submit" className="bg-[#002FA7] text-white">Affilier</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-10">
        <div className="bg-white border border-[#DEE2E6]" data-testid="adherents-list">
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]">
              <Building2 className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />
              Aucune entreprise adhérente. Cliquez sur "Affilier une entreprise".
            </div>
          ) : items.map((e) => (
            <div key={e._adherent_id} className="p-5 border-b border-[#DEE2E6] last:border-b-0 grid lg:grid-cols-12 gap-4 items-center" data-testid={`adherent-${e._adherent_id}`}>
              <div className="lg:col-span-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#002FA7] font-bold flex items-center gap-1.5"><Building2 className="h-3 w-3" /> {e.code}</div>
                <div className="font-display font-bold mt-1">{e.name}</div>
              </div>
              <div className="lg:col-span-3 text-xs text-[#495057]">{e.secteur || "—"}</div>
              <div className="lg:col-span-3 text-[10px] text-[#868E96] flex items-center gap-1.5 font-mono">
                <Calendar className="h-3 w-3" /> Adhérent depuis {e._adherent_since?.slice(0, 10) || "—"}
              </div>
              <div className="lg:col-span-2 flex justify-end">
                <Button size="sm" variant="outline" onClick={() => remove(e._adherent_id)} data-testid={`adherent-remove-${e._adherent_id}`} className="border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><Trash2 className="h-3 w-3 mr-1" /> Retirer</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
